/*
 * astrak-lead-tracking.js
 * ---------------------------------------------------------------------------
 * Records the route a visitor took through astrak.com, then stamps it onto any
 * Astrak lead form so the person triaging the inbox can see where the enquiry
 * came from without asking.
 *
 * Loaded sitewide via snippets/astrak-lead-scripts.liquid, which is rendered
 * from snippets/astrak-seo-head.liquid (first thing in <head> on every page).
 * It MUST run before astrak-lead-register.js.
 *
 * It fills the hidden inputs rendered by snippets/astrak-lead-context.liquid.
 * If this script fails to load the form still submits -- every field it touches
 * is optional and simply arrives empty.
 *
 * Storage: sessionStorage only (cleared when the tab closes). No cookies, no
 * third-party calls, no IP lookup, nothing that identifies a person before they
 * choose to submit the form.
 */
(function () {
  'use strict';

  var KEY = 'astrak_lead_ctx';
  var MAX_TRAIL = 12;

  function nowISO() {
    try { return new Date().toISOString(); } catch (e) { return ''; }
  }

  function read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function write(ctx) {
    try { sessionStorage.setItem(KEY, JSON.stringify(ctx)); } catch (e) { /* private mode */ }
  }

  function param(name) {
    try {
      return new URLSearchParams(window.location.search).get(name) || '';
    } catch (e) { return ''; }
  }

  /* First click id we can find -- tells you the ad platform even when UTMs are missing. */
  function clickId() {
    var ids = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'];
    for (var i = 0; i < ids.length; i++) {
      var v = param(ids[i]);
      if (v) return ids[i] + '=' + v;
    }
    return '';
  }

  /* Referrer is only meaningful on the first page of the session. */
  function externalReferrer() {
    var r = document.referrer || '';
    if (!r) return '(direct)';
    try {
      if (new URL(r).hostname === window.location.hostname) return '(internal)';
    } catch (e) { /* fall through */ }
    return r;
  }

  function init() {
    var ctx = read();

    if (!ctx) {
      ctx = {
        started: nowISO(),
        entry: window.location.href,
        referrer: externalReferrer(),
        utm_source: param('utm_source'),
        utm_medium: param('utm_medium'),
        utm_campaign: param('utm_campaign'),
        utm_term: param('utm_term'),
        utm_content: param('utm_content'),
        click_id: clickId(),
        trail: []
      };
    } else {
      /* Late-arriving campaign params (e.g. a second ad click in the same session)
         only fill gaps -- the original entry point is the one that earned the lead. */
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(function (k) {
        if (!ctx[k]) ctx[k] = param(k);
      });
      if (!ctx.click_id) ctx.click_id = clickId();
    }

    /* Shopify bounces back to the same URL with ?contact_posted=true after a
       successful submit. That is the same page re-rendered, not a new pageview:
       counting it inflates pages_viewed and puts a confusing
       "/products/x?contact_posted=true" entry in the middle of the trail. */
    if (!param('contact_posted')) {
      var here = window.location.pathname + (window.location.search || '');
      if (ctx.trail[ctx.trail.length - 1] !== here) ctx.trail.push(here);
      if (ctx.trail.length > MAX_TRAIL) ctx.trail = ctx.trail.slice(-MAX_TRAIL);
    }

    write(ctx);
    return ctx;
  }

  function setField(form, name, value) {
    var el = form.querySelector('[name="contact[' + name + ']"]');
    if (el && !el.value) el.value = value || '';
  }

  function stamp(form, ctx) {
    setField(form, 'entry_page', ctx.entry);
    setField(form, 'referrer', ctx.referrer);
    setField(form, 'utm_source', ctx.utm_source);
    setField(form, 'utm_medium', ctx.utm_medium);
    setField(form, 'utm_campaign', ctx.utm_campaign);
    setField(form, 'utm_term', ctx.utm_term);
    setField(form, 'utm_content', ctx.utm_content);
    setField(form, 'click_id', ctx.click_id);
    setField(form, 'path_trail', ctx.trail.join('  >  '));
    setField(form, 'pages_viewed', String(ctx.trail.length));

    /* The visitor's own device settings. Useful when someone in France is
       browsing the UK storefront in English -- the served market alone would
       send that lead to the wrong desk. */
    try {
      setField(form, 'browser_language', navigator.language || '');
      setField(form, 'browser_timezone', Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    } catch (e) { /* older browsers */ }

    /* One readable line, so the routing decision needs no scrolling. This is
       the ONLY triage surface in the notification email -- Shopify overrides
       contact[subject] with its own generic "New customer message on <date>"
       and there is no template to change it, so this string has to stand alone.
       Note there is deliberately no "via" prefix: bits already start with
       "campaign"/"from", which would read as "via from (direct)".

       stamp() runs TWICE per form (once on load, once on submit), so this
       append MUST be guarded. The guard is an explicit attribute, NOT a sniff
       for a substring of the appended text: a previous version tested for
       " | via " and silently started double-appending the moment that wording
       was dropped, producing "... | from (direct) | 11 pages viewed | from
       (direct) | 11 pages viewed". Do not replace this with a value check. */
    var summary = form.querySelector('[name="contact[routing_summary]"]');
    if (summary && summary.getAttribute('data-astrak-summary') !== '1') {
      summary.setAttribute('data-astrak-summary', '1');
      var bits = [];
      if (ctx.utm_campaign) bits.push('campaign ' + ctx.utm_campaign);
      if (ctx.referrer) bits.push('from ' + ctx.referrer);
      bits.push(ctx.trail.length + ' pages viewed');
      summary.value += ' | ' + bits.join(' | ');
    }
  }

  function bind(ctx) {
    var forms = document.querySelectorAll('form[data-astrak-lead], form.qform, form.fh-form');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.getAttribute('data-astrak-tracked') === '1') return;
      form.setAttribute('data-astrak-tracked', '1');
      /* Stamp on load so the values are present even if submit is triggered
         programmatically, and again on submit to catch late navigation. */
      stamp(form, ctx);
      form.addEventListener('submit', function () { stamp(form, ctx); });
    });
  }

  var ctx = init();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(ctx); });
  } else {
    bind(ctx);
  }
})();